import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiToggleRight } from 'react-icons/fi';
import { leaveTypeService } from '../../services/api';
import './Admin.css';

const LeaveTypeManagement = () => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    maxDaysPerYear: '',
    description: '',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await leaveTypeService.getAll({ limit: 1000 });
      setLeaveTypes(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load leave types');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Leave type name is required';
    }
    if (!formData.maxDaysPerYear || isNaN(formData.maxDaysPerYear) || formData.maxDaysPerYear <= 0) {
      newErrors.maxDaysPerYear = 'Max days must be a positive number';
    }
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenForm = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name,
        maxDaysPerYear: type.maxDaysPerYear,
        description: type.description || '',
        isActive: type.isActive !== false,
      });
    } else {
      setEditingType(null);
      setFormData({
        name: '',
        maxDaysPerYear: '',
        description: '',
        isActive: true,
      });
    }
    setFormErrors({});
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingType(null);
    setFormData({
      name: '',
      maxDaysPerYear: '',
      description: '',
      isActive: true,
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        maxDaysPerYear: parseInt(formData.maxDaysPerYear),
        description: formData.description,
        isActive: formData.isActive,
      };

      if (editingType) {
        await leaveTypeService.update(editingType.id, payload);
      } else {
        await leaveTypeService.create(payload);
      }

      await fetchLeaveTypes();
      handleCloseForm();
    } catch (err) {
      setFormErrors({
        submit: err.response?.data?.message || 'Failed to save leave type',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (type) => {
    try {
      setSaving(true);
      await leaveTypeService.update(type.id, {
        ...type,
        isActive: !type.isActive,
      });
      await fetchLeaveTypes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update leave type');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading leave types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Leave Type Management</h1>
        <button className="btn btn-primary" onClick={() => handleOpenForm()}>
          <FiPlus /> Add Leave Type
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {leaveTypes.length === 0 ? (
        <div className="no-results">
          <p>No leave types found. Create one to get started.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Leave Type</th>
                <th>Max Days Per Year</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leaveTypes.map(type => (
                <tr key={type.id}>
                  <td className="font-weight-bold">{type.name}</td>
                  <td>{type.maxDaysPerYear}</td>
                  <td>{type.description || '-'}</td>
                  <td>
                    <span className={`status-badge ${type.isActive ? 'active' : 'inactive'}`}>
                      {type.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleOpenForm(type)}
                      >
                        <FiEdit2 /> Edit
                      </button>
                      <button
                        className={`btn btn-sm ${type.isActive ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => handleToggleActive(type)}
                        disabled={saving}
                      >
                        <FiToggleRight /> {type.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCloseForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingType ? 'Edit Leave Type' : 'Add Leave Type'}</h2>
            </div>

            {formErrors.submit && <div className="error-banner">{formErrors.submit}</div>}

            <form onSubmit={handleSubmit} className="leave-type-form">
              <div className="form-group">
                <label htmlFor="name">Leave Type Name *</label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    if (formErrors.name) setFormErrors(prev => ({ ...prev, name: '' }));
                  }}
                  placeholder="e.g., Annual Leave"
                  className={formErrors.name ? 'input-error' : ''}
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="maxDaysPerYear">Max Days Per Year *</label>
                <input
                  id="maxDaysPerYear"
                  type="number"
                  value={formData.maxDaysPerYear}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, maxDaysPerYear: e.target.value }));
                    if (formErrors.maxDaysPerYear) {
                      setFormErrors(prev => ({ ...prev, maxDaysPerYear: '' }));
                    }
                  }}
                  placeholder="e.g., 20"
                  min="1"
                  className={formErrors.maxDaysPerYear ? 'input-error' : ''}
                />
                {formErrors.maxDaysPerYear && (
                  <span className="error-message">{formErrors.maxDaysPerYear}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description"
                  rows="3"
                />
              </div>

              <div className="form-group checkbox">
                <input
                  id="active"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                <label htmlFor="active">Active</label>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseForm}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? (
                    <>
                      <div className="inline-spinner"></div> Saving...
                    </>
                  ) : (
                    <>
                      <FiPlus /> Save Leave Type
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveTypeManagement;
